import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Grid, Box, Typography, CircularProgress, LinearProgress
} from '@mui/material';
import {
    People as UsersIcon,
    Groups as TeamIcon,
    Business as CompanyIcon,
    ElectricBolt as TariffIcon,
    Folder as ProjectIcon,
    Description as ScenarioIcon,
    Memory as SolverIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Schedule as PendingIcon,
    TrendingUp as TrendingIcon,
    Settings as SettingsIcon,
    Visibility as ActivityIcon,
} from '@mui/icons-material';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth, vortxTypography } from '@vortx/shared-ui';
import { useThemeMode } from '@vortx/shared-ui';
import { API_BASE_URL } from '../../services/api';

// Stat card component
const StatCard = ({ title, value, subtitle, icon: Icon, color = '#5fd4c4', onClick, isRetro }) => (
    <Box
        onClick={onClick}
        sx={{
            padding: 2.5,
            background: isRetro ? 'rgba(0, 0, 0, 0.4)' : 'background.paper',
            border: isRetro ? `1px solid rgba(63, 106, 100, 0.4)` : '1px solid',
            borderColor: isRetro ? undefined : 'divider',
            borderRadius: isRetro ? 0 : 2,
            cursor: onClick ? 'pointer' : 'default',
            transition: 'all 0.2s ease',
            '&:hover': onClick ? {
                borderColor: color,
                background: isRetro ? `${color}10` : undefined,
            } : {},
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box>
                <Typography sx={{
                    fontFamily: isRetro ? vortxTypography.mono : 'inherit',
                    fontSize: isRetro ? '0.65rem' : '0.75rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: isRetro ? '0.1em' : '0.05em',
                    color: isRetro ? '#888' : 'text.secondary',
                    mb: 1,
                }}>
                    {title}
                </Typography>
                <Typography sx={{
                    fontFamily: isRetro ? vortxTypography.heading : 'inherit',
                    fontSize: isRetro ? '2rem' : '1.75rem',
                    fontWeight: 700,
                    color: isRetro ? color : 'text.primary',
                    lineHeight: 1,
                    textShadow: isRetro ? `0 0 20px ${color}40` : 'none',
                }}>
                    {value}
                </Typography>
                {subtitle && (
                    <Typography sx={{
                        fontFamily: isRetro ? vortxTypography.mono : 'inherit',
                        fontSize: '0.75rem',
                        color: isRetro ? '#666' : 'text.secondary',
                        mt: 0.5,
                    }}>
                        {subtitle}
                    </Typography>
                )}
            </Box>
            <Box sx={{
                color: color,
                opacity: 0.8,
                filter: isRetro ? `drop-shadow(0 0 6px ${color}60)` : 'none',
            }}>
                <Icon sx={{ fontSize: 28 }} />
            </Box>
        </Box>
    </Box>
);

// Progress stat card for things like seats used
const ProgressStatCard = ({ title, used, total, icon: Icon, color = '#5fd4c4', onClick, isRetro }) => {
    const percentage = total > 0 ? (used / total) * 100 : 0;
    const isNearLimit = percentage >= 80;

    return (
        <Box
            onClick={onClick}
            sx={{
                padding: 2.5,
                background: isRetro ? 'rgba(0, 0, 0, 0.4)' : 'background.paper',
                border: isRetro ? `1px solid rgba(63, 106, 100, 0.4)` : '1px solid',
                borderColor: isRetro ? undefined : 'divider',
                borderRadius: isRetro ? 0 : 2,
                cursor: onClick ? 'pointer' : 'default',
                transition: 'all 0.2s ease',
                '&:hover': onClick ? {
                    borderColor: color,
                    background: isRetro ? `${color}10` : undefined,
                } : {},
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                <Typography sx={{
                    fontFamily: isRetro ? vortxTypography.mono : 'inherit',
                    fontSize: isRetro ? '0.65rem' : '0.75rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: isRetro ? '0.1em' : '0.05em',
                    color: isRetro ? '#888' : 'text.secondary',
                }}>
                    {title}
                </Typography>
                <Box sx={{
                    color: color,
                    opacity: 0.8,
                    filter: isRetro ? `drop-shadow(0 0 6px ${color}60)` : 'none',
                }}>
                    <Icon sx={{ fontSize: 20 }} />
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.5 }}>
                <Typography sx={{
                    fontFamily: isRetro ? vortxTypography.heading : 'inherit',
                    fontSize: isRetro ? '2rem' : '1.75rem',
                    fontWeight: 700,
                    color: isRetro ? color : 'text.primary',
                    lineHeight: 1,
                    textShadow: isRetro ? `0 0 20px ${color}40` : 'none',
                }}>
                    {used}
                </Typography>
                <Typography sx={{
                    fontFamily: isRetro ? vortxTypography.mono : 'inherit',
                    fontSize: '0.85rem',
                    color: isRetro ? '#666' : 'text.secondary',
                }}>
                    / {total}
                </Typography>
            </Box>
            <LinearProgress
                variant="determinate"
                value={percentage}
                sx={{
                    height: 6,
                    borderRadius: isRetro ? 0 : 3,
                    backgroundColor: isRetro ? 'rgba(63, 106, 100, 0.3)' : 'action.hover',
                    '& .MuiLinearProgress-bar': {
                        backgroundColor: isNearLimit ? '#f5a623' : color,
                        borderRadius: isRetro ? 0 : 3,
                    },
                }}
            />
            {isNearLimit && (
                <Typography sx={{
                    fontFamily: isRetro ? vortxTypography.mono : 'inherit',
                    fontSize: '0.7rem',
                    color: '#f5a623',
                    mt: 1,
                }}>
                    {percentage >= 100 ? 'Limit reached' : 'Approaching limit'}
                </Typography>
            )}
        </Box>
    );
};

// Status indicator component
const StatusIndicator = ({ status, label, isRetro }) => {
    const statusConfig = {
        healthy: { color: '#00ff41', icon: SuccessIcon, label: label || 'Healthy' },
        warning: { color: '#f5a623', icon: PendingIcon, label: label || 'Warning' },
        error: { color: '#e74c3c', icon: ErrorIcon, label: label || 'Error' },
        loading: { color: '#5fd4c4', icon: null, label: label || 'Loading' },
    };

    const config = statusConfig[status] || statusConfig.healthy;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {status === 'loading' ? (
                <CircularProgress size={14} sx={{ color: config.color }} />
            ) : (
                <Box sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: config.color,
                    boxShadow: isRetro ? `0 0 8px ${config.color}` : 'none',
                }} />
            )}
            <Typography sx={{
                fontFamily: isRetro ? vortxTypography.mono : 'inherit',
                fontSize: '0.75rem',
                color: config.color,
            }}>
                {config.label}
            </Typography>
        </Box>
    );
};

// Quick action card
const QuickActionCard = ({ title, description, icon: Icon, onClick, color = '#b28c50', isRetro }) => (
    <Box
        onClick={onClick}
        sx={{
            padding: 2,
            background: isRetro ? 'rgba(0, 0, 0, 0.3)' : 'background.paper',
            border: isRetro ? `1px solid ${color}40` : '1px solid',
            borderColor: isRetro ? undefined : 'divider',
            borderRadius: isRetro ? 0 : 2,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
                borderColor: color,
                background: isRetro ? `${color}15` : undefined,
            },
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
                color: color,
                filter: isRetro ? `drop-shadow(0 0 4px ${color}60)` : 'none',
            }}>
                <Icon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
                <Typography sx={{
                    fontFamily: isRetro ? vortxTypography.mono : 'inherit',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: isRetro ? '#c8c8c8' : 'text.primary',
                }}>
                    {title}
                </Typography>
                <Typography sx={{
                    fontFamily: isRetro ? vortxTypography.mono : 'inherit',
                    fontSize: '0.7rem',
                    color: isRetro ? '#666' : 'text.secondary',
                }}>
                    {description}
                </Typography>
            </Box>
        </Box>
    </Box>
);

// Section header
const SectionHeader = ({ title, isRetro }) => (
    <Typography sx={{
        fontFamily: isRetro ? vortxTypography.heading : 'inherit',
        fontSize: isRetro ? '0.9rem' : '1rem',
        fontWeight: 700,
        color: isRetro ? '#b28c50' : 'text.primary',
        textTransform: isRetro ? 'uppercase' : 'none',
        letterSpacing: isRetro ? '0.1em' : 'normal',
        mb: 2,
        pb: 1,
        borderBottom: isRetro ? '1px solid rgba(178, 140, 80, 0.3)' : 'none',
    }}>
        {title}
    </Typography>
);

const AdminDashboard = () => {
    const { currentUser, userProfile } = useAuth();
    const { isRetro } = useThemeMode();
    const navigate = useNavigate();
    const isSuperAdmin = userProfile?.is_super_admin;

    // State for fetched data
    const [loading, setLoading] = useState(true);
    const [teamData, setTeamData] = useState({ users: [], seats: { used: 0, total: 0 } });
    const [projectStats, setProjectStats] = useState({ projects: 0, scenarios: 0 });
    const [platformStats, setPlatformStats] = useState(null);
    const [solverStatus, setSolverStatus] = useState({ status: 'loading', jobs: [] });

    // Fetch company and team data
    useEffect(() => {
        const fetchData = async () => {
            if (!currentUser) {
                setLoading(false);
                return;
            }

            try {
                const token = await currentUser.getIdToken();

                // Fetch team/users data
                const usersRes = await fetch(`${API_BASE_URL}/api/v1/admin/my-company/users`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (usersRes.ok) {
                    const usersData = await usersRes.json();
                    setTeamData(prev => ({ ...prev, users: usersData.users || [] }));
                }

                // Fetch seats data
                const seatsRes = await fetch(`${API_BASE_URL}/api/v1/admin/my-company/seats`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (seatsRes.ok) {
                    const seatsData = await seatsRes.json();
                    const seats = seatsData.seats || [];
                    const usedSeats = seats.filter(s => s.assigned_to).length;
                    setTeamData(prev => ({
                        ...prev,
                        seats: { used: usedSeats, total: seats.length }
                    }));
                }

                // Fetch projects for stats
                const projectsRes = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (projectsRes.ok) {
                    const projectsData = await projectsRes.json();
                    const projects = projectsData.projects || [];
                    // Count total scenarios
                    let totalScenarios = 0;
                    for (const project of projects.slice(0, 10)) {
                        const scenariosRes = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/${project.id}/scenarios`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (scenariosRes.ok) {
                            const scenariosData = await scenariosRes.json();
                            totalScenarios += (scenariosData.scenarios || []).length;
                        }
                    }
                    setProjectStats({ projects: projects.length, scenarios: totalScenarios });
                }

                // Super admin: fetch platform stats
                if (isSuperAdmin) {
                    // Fetch all companies
                    const companiesRes = await fetch(`${API_BASE_URL}/api/v1/admin/companies`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (companiesRes.ok) {
                        const companiesData = await companiesRes.json();
                        setPlatformStats(prev => ({
                            ...prev,
                            companies: (companiesData.companies || []).length
                        }));
                    }

                    // Fetch solver jobs
                    const solverRes = await fetch(`${API_BASE_URL}/api/site_dna/v1/solver/jobs`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (solverRes.ok) {
                        const solverData = await solverRes.json();
                        const jobs = solverData.jobs || [];
                        const runningJobs = jobs.filter(j => j.status === 'running' || j.status === 'pending').length;
                        const failedJobs = jobs.filter(j => j.status === 'failed').length;
                        setSolverStatus({
                            status: failedJobs > 0 ? 'warning' : 'healthy',
                            running: runningJobs,
                            failed: failedJobs,
                            total: jobs.length,
                        });
                    } else {
                        setSolverStatus({ status: 'healthy', running: 0, failed: 0, total: 0 });
                    }
                }

            } catch (err) {
                console.error('Failed to fetch admin dashboard data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [currentUser, isSuperAdmin]);

    if (loading) {
        return (
            <AdminLayout title="Admin Dashboard">
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress sx={{ color: isRetro ? '#5fd4c4' : 'primary.main' }} />
                </Box>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="Admin Dashboard">
            {/* Company Overview Section */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Company Overview" isRetro={isRetro} />
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <ProgressStatCard
                            title="Team Seats"
                            used={teamData.seats.used}
                            total={teamData.seats.total || 5}
                            icon={TeamIcon}
                            color="#5fd4c4"
                            onClick={() => navigate('/admin/team')}
                            isRetro={isRetro}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            title="Team Members"
                            value={teamData.users.length}
                            subtitle="Active users"
                            icon={UsersIcon}
                            color="#b28c50"
                            onClick={() => navigate('/admin/team')}
                            isRetro={isRetro}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            title="Projects"
                            value={projectStats.projects}
                            subtitle={`${projectStats.scenarios} scenarios`}
                            icon={ProjectIcon}
                            color="#9b59b6"
                            isRetro={isRetro}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            title="This Month"
                            value={projectStats.scenarios}
                            subtitle="Optimizations run"
                            icon={TrendingIcon}
                            color="#5fd4c4"
                            isRetro={isRetro}
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Super Admin: Platform Overview */}
            {isSuperAdmin && (
                <Box sx={{ mb: 4 }}>
                    <SectionHeader title="Platform Overview" isRetro={isRetro} />
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard
                                title="Companies"
                                value={platformStats?.companies || 0}
                                subtitle="Active accounts"
                                icon={CompanyIcon}
                                color="#e74c3c"
                                onClick={() => navigate('/admin/companies')}
                                isRetro={isRetro}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{
                                padding: 2.5,
                                background: isRetro ? 'rgba(0, 0, 0, 0.4)' : 'background.paper',
                                border: isRetro ? `1px solid rgba(63, 106, 100, 0.4)` : '1px solid',
                                borderColor: isRetro ? undefined : 'divider',
                                borderRadius: isRetro ? 0 : 2,
                                cursor: 'pointer',
                                '&:hover': {
                                    borderColor: '#5fd4c4',
                                },
                            }}
                            onClick={() => navigate('/admin/solver')}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography sx={{
                                        fontFamily: isRetro ? vortxTypography.mono : 'inherit',
                                        fontSize: isRetro ? '0.65rem' : '0.75rem',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: isRetro ? '0.1em' : '0.05em',
                                        color: isRetro ? '#888' : 'text.secondary',
                                    }}>
                                        Solver Status
                                    </Typography>
                                    <SolverIcon sx={{ fontSize: 20, color: '#5fd4c4', opacity: 0.8 }} />
                                </Box>
                                <StatusIndicator status={solverStatus.status} isRetro={isRetro} />
                                <Typography sx={{
                                    fontFamily: isRetro ? vortxTypography.mono : 'inherit',
                                    fontSize: '0.7rem',
                                    color: isRetro ? '#666' : 'text.secondary',
                                    mt: 1,
                                }}>
                                    {solverStatus.running || 0} running, {solverStatus.failed || 0} failed
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard
                                title="Tariffs"
                                value="--"
                                subtitle="Custom tariffs"
                                icon={TariffIcon}
                                color="#f5a623"
                                onClick={() => navigate('/admin/tariffs')}
                                isRetro={isRetro}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{
                                padding: 2.5,
                                background: isRetro ? 'rgba(0, 0, 0, 0.4)' : 'background.paper',
                                border: isRetro ? `1px solid rgba(63, 106, 100, 0.4)` : '1px solid',
                                borderColor: isRetro ? undefined : 'divider',
                                borderRadius: isRetro ? 0 : 2,
                            }}>
                                <Typography sx={{
                                    fontFamily: isRetro ? vortxTypography.mono : 'inherit',
                                    fontSize: isRetro ? '0.65rem' : '0.75rem',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: isRetro ? '0.1em' : '0.05em',
                                    color: isRetro ? '#888' : 'text.secondary',
                                    mb: 1,
                                }}>
                                    System Health
                                </Typography>
                                <StatusIndicator status="healthy" label="All systems operational" isRetro={isRetro} />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            )}

            {/* Quick Actions */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="Quick Actions" isRetro={isRetro} />
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                        <QuickActionCard
                            title="Invite Team Member"
                            description="Add a new user to your team"
                            icon={UsersIcon}
                            onClick={() => navigate('/admin/team')}
                            color="#5fd4c4"
                            isRetro={isRetro}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <QuickActionCard
                            title="Manage Tariffs"
                            description="Add or edit rate schedules"
                            icon={TariffIcon}
                            onClick={() => navigate('/admin/tariffs')}
                            color="#f5a623"
                            isRetro={isRetro}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <QuickActionCard
                            title="Company Settings"
                            description="Update company information"
                            icon={SettingsIcon}
                            onClick={() => navigate('/admin/company')}
                            color="#b28c50"
                            isRetro={isRetro}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <QuickActionCard
                            title="Activity Monitor"
                            description="View live user activity"
                            icon={ActivityIcon}
                            onClick={() => navigate('/admin/activity')}
                            color="#00ff41"
                            isRetro={isRetro}
                        />
                    </Grid>
                    {isSuperAdmin && (
                        <>
                            <Grid item xs={12} sm={6} md={4}>
                                <QuickActionCard
                                    title="View All Users"
                                    description="Manage platform users"
                                    icon={UsersIcon}
                                    onClick={() => navigate('/admin/users')}
                                    color="#e74c3c"
                                    isRetro={isRetro}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <QuickActionCard
                                    title="Solver Monitor"
                                    description="View optimization jobs"
                                    icon={SolverIcon}
                                    onClick={() => navigate('/admin/solver')}
                                    color="#5fd4c4"
                                    isRetro={isRetro}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <QuickActionCard
                                    title="Bug Reports"
                                    description="Review submitted issues"
                                    icon={ScenarioIcon}
                                    onClick={() => navigate('/admin/bugs')}
                                    color="#9b59b6"
                                    isRetro={isRetro}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </Box>
        </AdminLayout>
    );
};

export default AdminDashboard;
